import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiLock } from "react-icons/fi";
import { Button } from "../../../ui/Button";
import { PasswordField } from "../../../ui/PasswordField";
import * as S from "../Login.styled";
import { resetPassword } from "../../../Utils/authService";
import PasswordStrengthMeter from "../PasswordStrengthMeter";

const FIELD_ORDER = ["newPassword", "confirmPassword"];

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emailFromUrl = searchParams.get("email") || "";
  const tokenFromUrl = searchParams.get("token") || "";

  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "success" });

  const validate = (values) => {
    const next = {};
    const required = t("login.field_required", { defaultValue: "This field is required" });
    if (!values.newPassword) next.newPassword = required;
    else if (values.newPassword.length < 10) {
      next.newPassword = t("login.password_too_short", {
        defaultValue: "Password must be at least 10 characters.",
      });
    } else if (
      !/[A-Z]/.test(values.newPassword) ||
      !/[a-z]/.test(values.newPassword) ||
      !/[0-9]/.test(values.newPassword) ||
      !/[^a-zA-Z0-9]/.test(values.newPassword)
    ) {
      next.newPassword = t("login.password_weak", {
        defaultValue: "Add an uppercase, lowercase, number, and special character.",
      });
    }
    if (!values.confirmPassword) next.confirmPassword = required;
    else if (values.confirmPassword !== values.newPassword) {
      next.confirmPassword = t("login.passwords_mismatch", {
        defaultValue: "Passwords do not match.",
      });
    }
    return next;
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    window.setTimeout(() => {
      setNotification({ message: "", type: "success" });
    }, 4000);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = { ...form, [name]: value };
    setForm(next);
    if (Object.keys(touched).length) setErrors(validate(next));
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(form));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    setTouched({ newPassword: true, confirmPassword: true });
    if (Object.keys(validation).length > 0) {
      const first = FIELD_ORDER.find((key) => validation[key]);
      if (first) document.getElementById(first)?.focus();
      return;
    }
    if (!emailFromUrl || !tokenFromUrl) {
      showNotification(
        t("login.invalid_reset_link", {
          defaultValue: "Invalid reset link. Please request a new one.",
        }),
        "error"
      );
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({
        email: emailFromUrl,
        token: tokenFromUrl,
        newPassword: form.newPassword,
      });
      showNotification(
        t("login.reset_success", { defaultValue: "Password updated. Please log in." }),
        "success"
      );
      window.setTimeout(() => navigate("/login"), 1000);
    } catch (error) {
      setSubmitting(false);
      showNotification(
        t("login.reset_error", {
          defaultValue:
            "Reset failed. The link may have expired. Please request a new one.",
        }),
        "error"
      );
    }
  };

  return (
    <S.Page>
      <S.FormWrapper>
        <S.FormHeader>
          <S.HeaderIcon aria-hidden="true">
            <FiLock />
          </S.HeaderIcon>
          <h1>{t("login.reset_title", { defaultValue: "Reset Password" })}</h1>
        </S.FormHeader>
        <S.Form onSubmit={handleSubmit} noValidate>
          {emailFromUrl && (
            <S.Subtitle>
              {t("login.resetting_for", { defaultValue: "Resetting password for:" })}{" "}
              <strong>{emailFromUrl}</strong>
            </S.Subtitle>
          )}
          <S.FieldGroup>
            <PasswordField
              id="newPassword"
              name="newPassword"
              label={t("login.new_password", { defaultValue: "New Password" })}
              placeholder={t("login.password_placeholder", { defaultValue: "New password" })}
              autoComplete="new-password"
              value={form.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              errorText={touched.newPassword ? errors.newPassword : undefined}
              required
            />
            <PasswordStrengthMeter password={form.newPassword} />
          </S.FieldGroup>
          <PasswordField
            id="confirmPassword"
            name="confirmPassword"
            label={t("login.confirm_password", { defaultValue: "Confirm Password" })}
            placeholder={t("login.confirm_password_placeholder", { defaultValue: "Confirm password" })}
            autoComplete="new-password"
            value={form.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={touched.confirmPassword ? errors.confirmPassword : undefined}
            required
          />
          {notification.message ? (
            <S.Notification $type={notification.type} role="status">
              {notification.message}
            </S.Notification>
          ) : null}
          <Button type="submit" variant="primary" fullWidth loading={submitting}>
            {t("login.reset_submit", { defaultValue: "Reset Password" })}
          </Button>
        </S.Form>
        <S.ToggleText>
          {t("login.toggle_existing", { defaultValue: "Already have an account?" })}{" "}
          <S.ToggleAction type="button" onClick={() => navigate("/login")}>
            {t("login.toggle_login", { defaultValue: "Log In" })}
          </S.ToggleAction>
        </S.ToggleText>
      </S.FormWrapper>
    </S.Page>
  );
};

export default ResetPasswordPage;
